import { Checkbox, Input, Select, SelectItem, Textarea } from "@heroui/react";
import { FloppyDisk, X } from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { useState } from "react";
import DirectSearchComponent from "shared/components/searchBar/components/directSearchComponent";
import { UserSelection } from "shared/components/userSelection/userSelection";
import {
  LegalProcedure,
  LegalProcedureStatus,
  LegalProcedureType,
  PartyRole,
  LEGAL_PROCEDURE_STATUS_LABELS,
  LEGAL_PROCEDURE_TYPE_LABELS,
  PARTY_ROLE_LABELS,
} from "shared/types/legal-procedure";
import { ResourceType } from "shared/types/resource";
import { ClerkUser } from "shared/types/user";

interface LegalProcedureEditFormProps {
  procedure: LegalProcedure;
  onSave: (updates: Record<string, unknown>) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}

const STATUS_OPTIONS = Object.entries(LEGAL_PROCEDURE_STATUS_LABELS).map(
  ([key, label]) => ({ key, label })
);

const TYPE_OPTIONS = Object.entries(LEGAL_PROCEDURE_TYPE_LABELS).map(
  ([key, label]) => ({ key, label })
);

const PARTY_ROLE_OPTIONS = Object.entries(PARTY_ROLE_LABELS).map(
  ([key, label]) => ({ key, label })
);

export const LegalProcedureEditForm = ({
  procedure,
  onSave,
  onCancel,
  isSaving,
}: LegalProcedureEditFormProps) => {
  const [status, setStatus] = useState<LegalProcedureStatus>(procedure.status);
  const [type, setType] = useState<LegalProcedureType>(procedure.type);
  const [partyRole, setPartyRole] = useState<PartyRole>(
    procedure.partyRole || PartyRole.EISENDE
  );
  const [caseNumber, setCaseNumber] = useState(procedure.caseNumber || "");
  const [court, setCourt] = useState(procedure.court || "");
  const [claimedAmount, setClaimedAmount] = useState(
    procedure.claimedAmount ? String(procedure.claimedAmount) : ""
  );
  const [awardedAmount, setAwardedAmount] = useState(
    procedure.awardedAmount ? String(procedure.awardedAmount) : ""
  );
  const [uitvoerbaarVerklaard, setUitvoerbaarVerklaard] = useState(
    procedure.uitvoerbaarVerklaard
  );
  const [lawyerId, setLawyerId] = useState<number | null>(
    procedure.lawyerId || null
  );
  const [lawyerReference, setLawyerReference] = useState(
    procedure.lawyerReference || ""
  );
  const [dossiervoorbereidingUserId, setDossiervoorbereidingUserId] = useState<
    string | null
  >(procedure.dossiervoorbereidingUserId || null);
  const [pleitenUserId, setPleitenUserId] = useState<string | null>(
    procedure.pleitenUserId || null
  );
  const [remarks, setRemarks] = useState(procedure.remarks || "");
  const [outcome, setOutcome] = useState(procedure.outcome || "");

  const hasLawyer = !!lawyerId;

  const handleSave = async () => {
    await onSave({
      status,
      type,
      partyRole,
      caseNumber: caseNumber || null,
      court: court || null,
      claimedAmount: claimedAmount ? parseFloat(claimedAmount) : null,
      awardedAmount: awardedAmount ? parseFloat(awardedAmount) : null,
      uitvoerbaarVerklaard,
      lawyerId: lawyerId || null,
      lawyerReference: lawyerReference || null,
      dossiervoorbereidingUserId: dossiervoorbereidingUserId || null,
      pleitenUserId: hasLawyer ? null : pleitenUserId || null,
      remarks: remarks || null,
      outcome: outcome || null,
    });
  };

  return (
    <div className="flex flex-col gap-3 py-3">
      <div className="grid grid-cols-3 gap-3">
        <Select
          label="Status"
          selectedKeys={[status]}
          onSelectionChange={(keys) => {
            const key = Array.from(keys)[0] as LegalProcedureStatus;
            if (key) setStatus(key);
          }}
          size="sm"
        >
          {STATUS_OPTIONS.map((option) => (
            <SelectItem key={option.key}>{option.label}</SelectItem>
          ))}
        </Select>

        <Select
          label="Type procedure"
          selectedKeys={[type]}
          onSelectionChange={(keys) => {
            const key = Array.from(keys)[0] as LegalProcedureType;
            if (key) setType(key);
          }}
          size="sm"
        >
          {TYPE_OPTIONS.map((option) => (
            <SelectItem key={option.key}>{option.label}</SelectItem>
          ))}
        </Select>

        <Select
          label="Onze rol"
          selectedKeys={[partyRole]}
          onSelectionChange={(keys) => {
            const key = Array.from(keys)[0] as PartyRole;
            if (key) setPartyRole(key);
          }}
          size="sm"
        >
          {PARTY_ROLE_OPTIONS.map((option) => (
            <SelectItem key={option.key}>{option.label}</SelectItem>
          ))}
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Rolnummer / Zaaknummer"
          placeholder="Optioneel..."
          value={caseNumber}
          onValueChange={setCaseNumber}
          size="sm"
        />
        <Input
          label="Rechtbank"
          placeholder="Naam / locatie..."
          value={court}
          onValueChange={setCourt}
          size="sm"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Gevorderd bedrag"
          type="number"
          placeholder="0,00"
          value={claimedAmount}
          onValueChange={setClaimedAmount}
          size="sm"
          startContent={
            <span className="text-xs text-neutral-secondary-mid">&#8364;</span>
          }
        />
        <Input
          label="Toegekend bedrag"
          type="number"
          placeholder="0,00"
          value={awardedAmount}
          onValueChange={setAwardedAmount}
          size="sm"
          startContent={
            <span className="text-xs text-neutral-secondary-mid">&#8364;</span>
          }
        />
      </div>

      <div className="flex items-center gap-2 pl-1">
        <Checkbox
          isSelected={uitvoerbaarVerklaard}
          onValueChange={setUitvoerbaarVerklaard}
          size="sm"
        >
          <span className="text-xs">Uitvoerbaar verklaard</span>
        </Checkbox>
      </div>

      {/* Lawyer selection */}
      <div className="flex flex-col gap-1">
        <span className="text-xs text-neutral-secondary-mid">Advocaat</span>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <DirectSearchComponent
              resourceFilter={[ResourceType.CONTACT]}
              onSelect={(resource) => setLawyerId(Number(resource.resourceId))}
              isCleared={() => setLawyerId(null)}
              label="Zoek of maak advocaat aan..."
              initialValue={procedure.lawyerId || undefined}
              initialValueType={procedure.lawyerId ? "id" : undefined}
              initialResource={
                procedure.lawyer
                  ? {
                      resourceId: String(procedure.lawyer.id),
                      type: ResourceType.CONTACT,
                      name: `${procedure.lawyer.firstName} ${procedure.lawyer.lastName}`,
                    }
                  : undefined
              }
            />
          </div>
          {hasLawyer && (
            <button
              onClick={() => setLawyerId(null)}
              className="p-1 rounded hover:bg-red-50 text-neutral-secondary-mid hover:text-red-500"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Lawyer reference */}
      {hasLawyer && (
        <Input
          label="Referentie advocaat"
          placeholder="Dossiernummer advocaat..."
          value={lawyerReference}
          onValueChange={setLawyerReference}
          size="sm"
        />
      )}

      {/* User selections */}
      <div className={`grid ${hasLawyer ? "grid-cols-1" : "grid-cols-2"} gap-3`}>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-neutral-secondary-mid">
            Dossiervoorbereiding door
          </span>
          <div className="flex items-center gap-2 min-h-[32px]">
            <UserSelection
              defaultUser={dossiervoorbereidingUserId}
              onUserSelect={(user: ClerkUser) =>
                setDossiervoorbereidingUserId(user?.id ? String(user.id) : null)
              }
            />
            {dossiervoorbereidingUserId && (
              <button
                onClick={() => setDossiervoorbereidingUserId(null)}
                className="p-0.5 rounded hover:bg-red-50 text-neutral-secondary-mid hover:text-red-500"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>
        {!hasLawyer && (
          <div className="flex flex-col gap-1">
            <span className="text-xs text-neutral-secondary-mid">
              Wie gaat pleiten in rechtbank
            </span>
            <div className="flex items-center gap-2 min-h-[32px]">
              <UserSelection
                defaultUser={pleitenUserId}
                onUserSelect={(user: ClerkUser) =>
                  setPleitenUserId(user?.id ? String(user.id) : null)
                }
              />
              {pleitenUserId && (
                <button
                  onClick={() => setPleitenUserId(null)}
                  className="p-0.5 rounded hover:bg-red-50 text-neutral-secondary-mid hover:text-red-500"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>
        )}
        {hasLawyer && (
          <p className="text-[11px] text-neutral-secondary-mid italic">
            Advocaat zal pleiten in de rechtbank.
          </p>
        )}
      </div>

      <Textarea
        label="Opmerkingen"
        placeholder="Optionele opmerkingen..."
        value={remarks}
        onValueChange={setRemarks}
        size="sm"
        minRows={2}
      />

      <Textarea
        label="Uitkomst / resultaat"
        placeholder="Optioneel..."
        value={outcome}
        onValueChange={setOutcome}
        size="sm"
        minRows={2}
      />

      <div className="flex justify-end gap-2">
        <ButtonNew
          variant="secondaryNeutral"
          label="Annuleren"
          onClick={onCancel}
        />
        <ButtonNew
          variant="primaryMoss"
          icon={<FloppyDisk size={12} />}
          label={isSaving ? "Opslaan..." : "Opslaan"}
          onClick={handleSave}
          isDisabled={isSaving}
        />
      </div>
    </div>
  );
};
